import {
    filterShapeAttr, generateCustomStyles,
    getMappingSymbol,
    getShapeAttr,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import { DocSizeType, getMaxLogicSize, getTextLayout, twipsToPx } from '@tencent/alloy-editor';
import { CustomLegendPosition } from '@tencent/chart/lib/chart-ui/interface';
import { IChartLegend } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import { STLegendPos } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { mergeTextBodyStyle } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/text';
import type { ILegendMarker } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import type { ChartContext } from '@src/slide/domain/ui-model/element-modeles/chart-models/context';
import type { ICommonConfig, ICustomLayout } from '@tencent/chart/lib/chart-ui/interface';
import type { MarkerCfg } from '@tencent/chart/lib/antv/g2/interface';
import type {
    IChartCommonConfigHelper,
    IChartConfigHelper,
    ILegendItem,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-config.interface';
import type { IBaseChartProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-props.interface';
import type { ChartLayoutInfo } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-component.interface';
import type { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { MarkerType } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import isEmpty from 'lodash/isEmpty';
import { ShapeAttrs } from '@tencent/chart/lib/antv/g2/dependents';
import { IStrokeStyle } from '@tencent/alloy-graphics';

const CustomLegendPositionMapping: Record<STLegendPos, CustomLegendPosition> = {
    [STLegendPos.b]: CustomLegendPosition.BOTTOM,
    [STLegendPos.l]: CustomLegendPosition.LEFT,
    [STLegendPos.t]: CustomLegendPosition.TOP,
    [STLegendPos.tr]: CustomLegendPosition.TOP_RIGHT,
    [STLegendPos.r]: CustomLegendPosition.RIGHT,
    [STLegendPos.PBDEFAULT]: CustomLegendPosition.BOTTOM,
};

/**
 * 获取公用图表字段中，图例相关配置信息
 */
export class LegendConfigHelper implements IChartCommonConfigHelper {
    private readonly chartHelper: IChartConfigHelper;
    private readonly chartProps: IBaseChartProps;
    private readonly chartLayout: ChartLayoutInfo;

    /**
     *
     * @param chartHelper chart配置处理类
     * @param context
     */
    public constructor(chartHelper: IChartConfigHelper, private context: ChartContext) {
        this.chartHelper = chartHelper;
        this.chartProps = context.getChartModel();
        this.chartLayout = chartHelper.layout;
    }

    public getCommonConfig(): ICommonConfig {
        return { legend: this.generateLegendConfig() };
    }

    /**
     * 处理legend数据。
     * 1. 第一次计算无视自定义layout，先计算占位。
     * 2. 第二次计算包含自定义layout，重新排版, 计算offset
     */
    private generateLegendConfig(): ICommonConfig['legend'] {
        const legend = this.context.getPropertyModule('legend');
        if (!legend) return false;

        const { layout, props, position: legendPos = STLegendPos.r, overlay = true } = (legend as IChartLegend) || {};
        const legendItems: ILegendItem[] = this.context.getPropertyModule('legend')?.items.map((item, index) => {
            const docElement = this.getTextDocElement(item as ILegendItem, index);
            return {
                name: item.name,
                marker: this.getMarkerConfig(item.marker) as unknown as MarkerCfg,
                docElement,
                style: { customStyles: { text: docElement } },
            };
        }) || [];

        const { fill, border, ...rest } = getShapeAttr(props);
        const titleHeight = this.chartLayout.getTitleOccupiedHeight();

        return {
            custom: true,
            items: legendItems as any[],
            position: CustomLegendPositionMapping[legendPos || ''] || CustomLegendPosition.BOTTOM,
            customLayout: layout as ICustomLayout,
            style: {
                fill,
                ...border,
                customStyles: generateCustomStyles(rest),
            },
            overlay,
            margin: [titleHeight],
            chartBox: this.chartLayout.g2CanvasBox,
            onMarkerMeasured: (newMarker) => {
                const { customStyles = { }, lineWidth } = newMarker?.style as ShapeAttrs || { };
                const { strokeStyle } = customStyles;
                if (!isEmpty(strokeStyle) && typeof lineWidth !== 'undefined') {  // 更新透传数据的线宽
                    (strokeStyle as IStrokeStyle).lineWidth = lineWidth;
                }
            },
            getReLayoutLegendItemBox: (item, maxTextWidth) => {
                // 让内容自适应大小, 计算出来的尺寸为内容本身大小
                const fixDocElement = {
                    ...item.docElement, docSizeType: DocSizeType.WrapContent,
                };
                const textLayoutBox: any = getTextLayout(fixDocElement as any, maxTextWidth);
                const lineBoxes = textLayoutBox?.children[0]?.children;
                let textWidth = maxTextWidth;
                if (lineBoxes?.length <= 1) { // 未超出一行，则为自适应宽度，否则为最大宽度
                    textWidth = Math.ceil(getMaxLogicSize(textLayoutBox));
                }

                // 存储排版之后的文字
                const reLayoutName = lineBoxes?.map(line => line?.children?.map(textBox => textBox.text).join('')).join('\n');

                return {
                    rect: {
                        height: twipsToPx(textLayoutBox.rect.height),
                        width: twipsToPx(textLayoutBox.rect.width),
                    },
                    text: reLayoutName,
                    textWidth,
                };
            },
        };
    }

    private getMarkerConfig(marker: ILegendMarker) {
        const { fill, border, ...rest } = filterShapeAttr(getShapeAttr(
            marker.props,
            { isLine: marker.symbol === MarkerType.Line, filter: ['effects'] }, // marker不显示特效
        ));
        return {
            // 当前业务里有部分图表的marker的symbol是直接传的STMarkerStyleDmlC，比如散点图， 这里就直接转成g2的marker symbol类型
            // 有部分图表的symbol传递的是MarkerType类型（自定义的marker特征类型，就透传，会在chart内部做计算处理）
            symbol: getMappingSymbol(marker.symbol as any) || marker.symbol,
            style: {
                fill,
                ...border,
                customStyles: generateCustomStyles(rest),
            },
        };
    }

    private getTextDocElement(item: ILegendItem, index: number) {
        const { legend } = this.chartProps;

        const legendEntry = legend?.legendEntryList?.find(entry => entry.id === index);
        const paragraphList = LegendConfigHelper.getLegendParaList(
            item.name,
            legendEntry?.paragraphList || legend?.paragraphList || [],
        );
        const textBodyStyle = mergeTextBodyStyle(
            legendEntry?.textBodyStyle,
            legend?.textBodyStyle,
            { noDefaultPadding: true, anchorLeft: true },
        ) || { bodyStyle: { paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0 } };
        return EditorDataModel.convertFromTextModel(
            { paragraphList, textBodyStyle },
            { noConvertBack: true },
        );
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private static getLegendParaList(text: string, paraList: IParagraph[]): IParagraph[] {
        return [{
            ...paraList?.[0],
            textRuns: [{
                text,
                styles: {
                    ...(paraList?.[0]?.paragraphProps?.defaultRunProps || {}),
                },
                type: 'run',
            }],
        }];
    }
}
